import { useState } from 'react';
import { Badge, Tabs, TabsContent, TabsList, TabsTrigger } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import type { LibraryFormat, LibraryState, SpecValueInput, TextLimitInput } from '../model/types';
import { BreakingChangeDialog } from './BreakingChangeDialog';
import { GeneralTab } from './GeneralTab';
import { HistoryTab } from './HistoryTab';
import { SpecTab } from './SpecTab';
import { TextLimitsTab } from './TextLimitsTab';
import { useFormatSaver } from './useFormatSaver';

/* eslint-disable i18next/no-literal-string -- tab ids, not copy */
const TAB = {
  general: 'general',
  image: 'image',
  video: 'video',
  text: 'text',
  history: 'history',
} as const;
const MEDIA = { image: 'image', video: 'video' } as const;
/* eslint-enable i18next/no-literal-string */

interface FormatEditorProps {
  state: LibraryState;
  format: LibraryFormat;
}

/** The detail pane: one format, its canvas and every spec, edited in place and saved per change. */
export function FormatEditor({ state, format }: FormatEditorProps) {
  const { t } = useTranslation('admin');
  const [tab, setTab] = useState<string>(TAB.general);
  const { save, isSaving, confirmation } = useFormatSaver();

  const saveSpec = (mediaType: string, property: string, input: SpecValueInput | null) =>
    save(format.key, { revision: format.revision, specs: { [mediaType]: { [property]: input } } });
  const saveLimit = (field: string, input: TextLimitInput | null) =>
    save(format.key, { revision: format.revision, textLimits: { [field]: input } });

  return (
    <section aria-labelledby="format-editor-title" className="flex min-h-0 flex-col gap-md">
      <BreakingChangeDialog {...confirmation} />
      <header className="flex flex-wrap items-center gap-sm">
        <h2 id="format-editor-title" className="type-heading-sm">
          {format.name}
        </h2>
        <span className="text-muted-foreground tabular-nums">
          {format.w}×{format.h}
        </span>
        {format.status === 'deprecated' && (
          <Badge variant="outline">{t('admin.adFormats.deprecated')}</Badge>
        )}
      </header>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value={TAB.general}>{t('admin.adFormats.tabs.general')}</TabsTrigger>
          <TabsTrigger value={TAB.image}>{t('admin.adFormats.tabs.image')}</TabsTrigger>
          <TabsTrigger value={TAB.video}>{t('admin.adFormats.tabs.video')}</TabsTrigger>
          <TabsTrigger value={TAB.text}>{t('admin.adFormats.tabs.textLimits')}</TabsTrigger>
          <TabsTrigger value={TAB.history}>{t('admin.adFormats.tabs.history')}</TabsTrigger>
        </TabsList>
        <TabsContent value={TAB.general} className="pt-md">
          <GeneralTab
            format={format}
            isSaving={isSaving}
            onSave={(patch) => void save(format.key, { revision: format.revision, ...patch })}
          />
        </TabsContent>
        <TabsContent value={TAB.image} className="pt-md">
          <SpecTab
            state={state}
            format={format}
            mediaType="image"
            isSaving={isSaving}
            onSave={(property, input) => saveSpec(MEDIA.image, property, input)}
          />
        </TabsContent>
        <TabsContent value={TAB.video} className="pt-md">
          <SpecTab
            state={state}
            format={format}
            mediaType="video"
            isSaving={isSaving}
            onSave={(property, input) => saveSpec(MEDIA.video, property, input)}
          />
        </TabsContent>
        <TabsContent value={TAB.text} className="pt-md">
          <TextLimitsTab state={state} format={format} isSaving={isSaving} onSave={saveLimit} />
        </TabsContent>
        <TabsContent value={TAB.history} className="pt-md">
          <HistoryTab formatKey={format.key} />
        </TabsContent>
      </Tabs>
    </section>
  );
}
